import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme, getAvatarForUser } from '../context/ThemeContext';
import { adminAPI, adminScoreCardAPI } from '../api';
import { CurrencySymbol, CurrencyDisplay } from '../components/CurrencySymbol';
import { PlayerAvatar, RankBadge, FunScore, FunModeToggle } from '../components/FunMode';
import TournamentManager from '../components/TournamentManager';
import { 
    Users, Plus, Trash2, Key, CreditCard, History, FolderOpen,
    Menu, X, LogOut, RefreshCw, AlertCircle, CheckCircle,
    UserPlus, UserMinus, Download, FileSpreadsheet, FileText, Sun, Moon, Copy, Trophy, ChevronLeft, ChevronRight, BarChart3, Award, Settings, Wallet, PiggyBank
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Helper function to format number - show decimal only if needed
const formatNumber = (value) => {
    const num = parseFloat(value) || 0;
    const rounded = Math.round(num * 10) / 10;
    if (Number.isInteger(rounded)) {
        return rounded.toString();
    }
    return rounded.toFixed(1);
};

const AdminDashboard = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('users');
    const [users, setUsers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [adminBalance, setAdminBalance] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    // Modal states
    const [showCreateUser, setShowCreateUser] = useState(false);
    const [showCreateGroup, setShowCreateGroup] = useState(false);
    const [showResetPassword, setShowResetPassword] = useState(false);
    const [showAddBalance, setShowAddBalance] = useState(false);
    const [showDeleteUserConfirm, setShowDeleteUserConfirm] = useState(false);
    const [showDeleteGroupConfirm, setShowDeleteGroupConfirm] = useState(false);
    const [showAddToGroup, setShowAddToGroup] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedGroup, setSelectedGroup] = useState(null);
    
    // Form states
    const [newUsername, setNewUsername] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [newGroupName, setNewGroupName] = useState('');
    const [resetPassword, setResetPasswordValue] = useState('');
    const [balanceAmount, setBalanceAmount] = useState('');
    const [selectedUserId, setSelectedUserId] = useState('');
    
    // Duplicate scorecard states
    const [showDuplicates, setShowDuplicates] = useState(false);
    const [duplicateData, setDuplicateData] = useState(null);
    const [duplicateLoading, setDuplicateLoading] = useState(false);
    const [selectedGroupForDuplicates, setSelectedGroupForDuplicates] = useState(null);
    
    // Klasemen states
    const [showKlasmen, setShowKlasmen] = useState(false);
    const [klasmenGroup, setKlasmenGroup] = useState(null);
    const [klasmenData, setKlasmenData] = useState(null);
    const [klasmenLoading, setKlasmenLoading] = useState(false);
    const [klasmenMonth, setKlasmenMonth] = useState(new Date().getMonth() + 1);
    const [klasmenYear, setKlasmenYear] = useState(new Date().getFullYear());
    const [klasmenTabGroup, setKlasmenTabGroup] = useState(null); // For Klasemen tab
    
    // Player Statistics states
    const [showStatistics, setShowStatistics] = useState(false);
    const [statisticsGroup, setStatisticsGroup] = useState(null);
    const [statisticsData, setStatisticsData] = useState(null);
    const [statisticsLoading, setStatisticsLoading] = useState(false);
    const [statisticsMonth, setStatisticsMonth] = useState(new Date().getMonth() + 1);
    const [statisticsYear, setStatisticsYear] = useState(new Date().getFullYear());
    const [showDeleteScorecard, setShowDeleteScorecard] = useState(false);
    const [scorecardToDelete, setScorecardToDelete] = useState(null);
    
    // Admin Create Score Card states
    const [showAdminCreateScorecard, setShowAdminCreateScorecard] = useState(false);
    const [adminScorecardMonth, setAdminScorecardMonth] = useState(new Date().getMonth() + 1);
    const [adminScorecardYear, setAdminScorecardYear] = useState(new Date().getFullYear());
    const [adminSelectedUsers, setAdminSelectedUsers] = useState([]);
    const [adminScoreInputs, setAdminScoreInputs] = useState({});
    const [adminCreateLoading, setAdminCreateLoading] = useState(false);
    
    // Head-to-Head states
    const [h2hSelectedGroup, setH2hSelectedGroup] = useState(null);
    const [h2hSelectedPlayers, setH2hSelectedPlayers] = useState([]);
    const [h2hData, setH2hData] = useState(null);
    const [h2hLoading, setH2hLoading] = useState(false);
    
    // Operator & TK Settings states
    const [showGroupSettings, setShowGroupSettings] = useState(false);
    const [settingsGroup, setSettingsGroup] = useState(null);
    const [groupOperators, setGroupOperators] = useState([]);
    const [tkUserId, setTkUserId] = useState(null);
    const [tkEnabled, setTkEnabled] = useState(false);
    const [tkRules, setTkRules] = useState([]);
    const [settingsLoading, setSettingsLoading] = useState(false);
    
    // Chart states - removed

const MONTH_NAMES = [
    'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
    'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
];

// Helper function to get score color
const getScoreColorClass = (score) => {
    if (score > 0) return 'text-neon-green';
    if (score < 0) return 'text-red-500';
    return 'text-zinc-400';
};

    const { token, user, logout } = useAuth();
    const { isDark, toggleTheme, isFunMode } = useTheme();
    const navigate = useNavigate();

    useEffect(() => {
        loadData();
    }, [token]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [usersData, groupsData, transactionsData, balanceData] = await Promise.all([
                adminAPI.getUsers(token),
                adminAPI.getGroups(token),
                adminAPI.getTransactions(token),
                adminAPI.getBalance(token)
            ]);
            setUsers(usersData);
            setGroups(groupsData);
            setTransactions(transactionsData);
            setAdminBalance(balanceData.balance);
        } catch (err) {
            setError('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        try {
            await adminAPI.createUser(token, newUsername, newPassword);
            setSuccess('User created successfully');
            setShowCreateUser(false);
            setNewUsername('');
            setNewPassword('');
            loadData();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to create user');
        }
    };

    const handleCreateGroup = async (e) => {
        e.preventDefault();
        try {
            await adminAPI.createGroup(token, newGroupName);
            setSuccess('Group created successfully');
            setShowCreateGroup(false);
            setNewGroupName('');
            loadData();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to create group');
        }
    };

    const handleDeleteUser = async () => {
        try {
            await adminAPI.deleteUser(token, selectedUser.id);
            setSuccess('User deleted successfully');
            setShowDeleteUserConfirm(false);
            setSelectedUser(null);
            loadData();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to delete user');
        }
    };

    const handleDeleteGroup = async () => {
        try {
            await adminAPI.deleteGroup(token, selectedGroup.id);
            setSuccess('Group deleted successfully');
            setShowDeleteGroupConfirm(false);
            setSelectedGroup(null);
            loadData();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to delete group');
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        try {
            await adminAPI.resetUserPassword(token, selectedUser.id, resetPassword);
            setSuccess('Password reset successfully. User will need to setup again on next login.');
            setShowResetPassword(false);
            setResetPasswordValue('');
            setSelectedUser(null);
            loadData();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to reset password');
        }
    };

    const handleToggleFinance = async (userId) => {
        try {
            await adminAPI.toggleFinanceStatus(token, userId);
            setSuccess('Finance status updated');
            loadData();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to update finance status');
        }
    };

    const handleToggleHidden = async (userId) => {
        try {
            await adminAPI.toggleHiddenStatus(token, userId);
            setSuccess('Hidden status updated');
            loadData();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to update hidden status');
        }
    };

    const handleToggleHideZeroScores = async (groupId) => {
        try {
            await adminAPI.toggleHideZeroScores(token, groupId);
            setSuccess('Hide zero scores setting updated');
            loadData();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to update setting');
        }
    };

    const handleToggleGroupPublic = async (groupId) => {
        try {
            const result = await adminAPI.toggleGroupPublic(token, groupId);
            setSuccess(result.message);
            loadData();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to update public status');
        }
    };

    const handleAddBalance = async (e) => {
        e.preventDefault();
        const amount = parseFloat(balanceAmount);
        if (amount > adminBalance) {
            setError('Insufficient balance');
            return;
        }
        try {
            await adminAPI.addUserBalance(token, selectedUser.id, amount);
            setSuccess('Balance added successfully');
            setShowAddBalance(false);
            setBalanceAmount('');
            setSelectedUser(null);
            loadData();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to add balance');
        }
    };

    const handleAddToGroup = async (e) => {
        e.preventDefault();
        try {
            await adminAPI.addUserToGroup(token, selectedGroup.id, selectedUserId);
            setSuccess('User added to group successfully');
            setShowAddToGroup(false);
            setSelectedUserId('');
            loadData();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to add user to group');
        }
    };

    const handleRemoveFromGroup = async (groupId, userId) => {
        try {
            await adminAPI.removeUserFromGroup(token, groupId, userId);
            setSuccess('User removed from group');
            loadData();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to remove user from group');
        }
    };

    // Duplicate scorecard functions
    const handleCheckDuplicates = async (group) => {
        setSelectedGroupForDuplicates(group);
        setDuplicateLoading(true);
        setShowDuplicates(true);
        try {
            const data = await adminScoreCardAPI.getDuplicates(token, group.id);
            setDuplicateData(data);
        } catch (err) {
            setError(err.response?.data?.detail || 'Gagal mengecek duplicate');
            setShowDuplicates(false);
        } finally {
            setDuplicateLoading(false);
        }
    };

    const handleDeleteDuplicates = async () => {
        if (!selectedGroupForDuplicates) return;
        setDuplicateLoading(true);
        try {
            const result = await adminScoreCardAPI.deleteDuplicates(token, selectedGroupForDuplicates.id);
            setSuccess(`Berhasil menghapus ${result.deleted_count} duplicate score card`);
            setShowDuplicates(false);
            setDuplicateData(null);
        } catch (err) {
            setError(err.response?.data?.detail || 'Gagal menghapus duplicate');
        } finally {
            setDuplicateLoading(false);
        }
    };

    // Klasemen functions
    const handleOpenKlasmen = async (group) => {
        setKlasmenGroup(group);
        setShowKlasmen(true);
        await loadKlasmenData(group.id, klasmenMonth, klasmenYear);
    };

    const loadKlasmenData = async (groupId, month, year) => {
        setKlasmenLoading(true);
        try {
            const data = await adminScoreCardAPI.getScoreCards(token, groupId, month, year);
            // Get leaderboard data using admin endpoint instead of public
            const leaderboardRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/leaderboard/${groupId}?month=${month}&year=${year}&token=${token}`);
            const leaderboard = await leaderboardRes.json();
            setKlasmenData({
                scorecards: data,
                leaderboard: leaderboard
            });
        } catch (err) {
            setError('Gagal memuat data klasemen');
        } finally {
            setKlasmenLoading(false);
        }
    };

    // Player Statistics functions
    const handleOpenStatistics = async (group) => {
        setStatisticsGroup(group);
        setShowStatistics(true);
        await loadStatisticsData(group.id, statisticsMonth, statisticsYear);
    };

    const loadStatisticsData = async (groupId, month, year) => {
        setStatisticsLoading(true);
        try {
            const data = await adminScoreCardAPI.getPlayerStatistics(token, groupId, month, year);
            setStatisticsData(data);
        } catch (err) {
            setError('Gagal memuat statistik pemain');
        } finally {
            setStatisticsLoading(false);
        }
    };

    const handleStatisticsMonthChange = (direction) => {
        let newMonth = statisticsMonth + direction;
        let newYear = statisticsYear;
        
        if (newMonth > 12) {
            newMonth = 1;
            newYear++;
        } else if (newMonth < 1) {
            newMonth = 12;
            newYear--;
        }
        
        setStatisticsMonth(newMonth);
        setStatisticsYear(newYear);
        if (statisticsGroup) {
            loadStatisticsData(statisticsGroup.id, newMonth, newYear);
        }
    };

    const handleKlasmenMonthChange = (direction) => {
        let newMonth = klasmenMonth + direction;
        let newYear = klasmenYear;
        
        if (newMonth > 12) {
            newMonth = 1;
            newYear++;
        } else if (newMonth < 1) {
            newMonth = 12;
            newYear--;
        }
        
        setKlasmenMonth(newMonth);
        setKlasmenYear(newYear);
        if (klasmenGroup) {
            loadKlasmenData(klasmenGroup.id, newMonth, newYear);
        }
    };

    const handleDeleteScorecard = async () => {
        if (!scorecardToDelete) return;
        try {
            await adminScoreCardAPI.deleteScoreCard(token, scorecardToDelete.id);
            setSuccess('Score card berhasil dihapus');
            setShowDeleteScorecard(false);
            setScorecardToDelete(null);
            // Reload klasmen data
            if (klasmenGroup) {
                loadKlasmenData(klasmenGroup.id, klasmenMonth, klasmenYear);
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Gagal menghapus score card');
        }
    };

    // Admin Create Score Card functions
    const handleOpenAdminCreateScorecard = () => {
        setAdminSelectedUsers([]);
        setAdminScoreInputs({});
        setShowAdminCreateScorecard(true);
    };

    const handleAdminUserSelect = (userId, username) => {
        if (adminSelectedUsers.find(u => u.id === userId)) {
            setAdminSelectedUsers(adminSelectedUsers.filter(u => u.id !== userId));
            const newInputs = { ...adminScoreInputs };
            delete newInputs[userId];
            setAdminScoreInputs(newInputs);
        } else {
            setAdminSelectedUsers([...adminSelectedUsers, { id: userId, username }]);
            setAdminScoreInputs({ ...adminScoreInputs, [userId]: '' });
        }
    };

    const handleAdminScoreChange = (userId, value) => {
        setAdminScoreInputs({ ...adminScoreInputs, [userId]: value });
    };

    const calculateAdminTotal = () => {
        return Object.values(adminScoreInputs).reduce((sum, val) => {
            const num = parseFloat(val) || 0;
            return sum + Math.round(num * 10) / 10;
        }, 0);
    };

    const isAdminScoreTotalZero = () => {
        const total = calculateAdminTotal();
        return Math.abs(Math.round(total * 10) / 10) < 0.01;
    };

    const handleAdminCreateScorecard = async () => {
        if (adminSelectedUsers.length === 0) {
            setError('Pilih minimal satu user');
            return;
        }

        // Validate all scores are filled
        for (const u of adminSelectedUsers) {
            if (!adminScoreInputs[u.id] || adminScoreInputs[u.id] === '') {
                setError(`Masukkan skor untuk ${u.username}`);
                return;
            }
        }

        // Validate total is zero
        if (!isAdminScoreTotalZero()) {
            setError('Total skor harus sama dengan 0');
            return;
        }

        setAdminCreateLoading(true);
        try {
            const entries = adminSelectedUsers.map(u => ({
                user_id: u.id,
                username: u.username,
                score: Math.round(parseFloat(adminScoreInputs[u.id]) * 10) / 10
            }));

            await adminScoreCardAPI.createScoreCard(
                token, 
                klasmenGroup.id, 
                entries, 
                adminScorecardMonth, 
                adminScorecardYear
            );
            
            setSuccess(`Score card berhasil dibuat untuk ${MONTH_NAMES[adminScorecardMonth - 1]} ${adminScorecardYear}!`);
            setShowAdminCreateScorecard(false);
            setAdminSelectedUsers([]);
            setAdminScoreInputs({});
            
            // Reload klasmen data
            loadKlasmenData(klasmenGroup.id, klasmenMonth, klasmenYear);
        } catch (err) {
            setError(err.response?.data?.detail || 'Gagal membuat score card');
        } finally {
            setAdminCreateLoading(false);
        }
    };

    // Generate available months (current and past 12 months)
    const getAvailableMonths = () => {
        const months = [];
        const now = new Date();
        for (let i = 0; i < 12; i++) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            months.push({ month: d.getMonth() + 1, year: d.getFullYear() });
        }
        return months;
    };

    const handleExportCSV = async () => {
        try {
            const blob = await adminAPI.exportCSV(token);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'transactions.csv';
            a.click();
            window.URL.revokeObjectURL(url);
            setSuccess('CSV exported successfully');
        } catch (err) {
            setError('Failed to export CSV');
        }
    };

    const handleExportPDF = () => {
        const doc = new jsPDF();
        
        doc.setFontSize(18);
        doc.text('Transaction Report', 14, 22);
        doc.setFontSize(10);
        doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 30);
        
        const tableData = transactions.map(tx => {
            const date = new Date(tx.created_at);
            return [
                date.toLocaleDateString(),
                date.toLocaleTimeString(),
                tx.from_username,
                tx.to_username,
                `V ${tx.amount.toFixed(2)}`
            ];
        });

        doc.autoTable({
            head: [['Date', 'Time', 'From', 'To', 'Amount']],
            body: tableData,
            startY: 40,
            theme: 'grid',
            headStyles: { fillColor: [57, 255, 20], textColor: [0, 0, 0] },
            styles: { fontSize: 9 }
        });

        doc.save('transactions.pdf');
        setSuccess('PDF exported successfully');
    };

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const formatTime = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    };

    const getUsernameById = (userId) => {
        const foundUser = users.find(u => u.id === userId);
        return foundUser?.username || userId;
    };

    // Get users not in a specific group
    const getUsersNotInGroup = (group) => {
        return users.filter(u => !group.members.includes(u.id));
    };

    // Head-to-Head Statistics
    const loadH2HStatistics = async () => {
        if (!h2hSelectedGroup || h2hSelectedPlayers.length < 2) {
            setError('Pilih minimal 2 pemain untuk melihat statistik');
            return;
        }
        
        setH2hLoading(true);
        try {
            const data = await adminAPI.getHeadToHead(token, h2hSelectedGroup.id, h2hSelectedPlayers);
            setH2hData(data);
        } catch (err) {
            setError(err.response?.data?.detail || 'Gagal memuat statistik H2H');
        } finally {
            setH2hLoading(false);
        }
    };

    const toggleH2HPlayer = (userId) => {
        setH2hSelectedPlayers(prev => {
            if (prev.includes(userId)) {
                return prev.filter(id => id !== userId);
            } else {
                return [...prev, userId];
            }
        });
        setH2hData(null); // Reset data when selection changes
    };

    // === OPERATOR & TK SETTINGS FUNCTIONS ===
    const handleOpenGroupSettings = async (group) => {
        setSettingsGroup(group);
        setSettingsLoading(true);
        try {
            const settings = await adminAPI.getGroupSettings(token, group.id);
            setGroupOperators(settings.operators || []);
            setTkUserId(settings.tk_user_id || null);
            setTkEnabled(settings.tk_enabled || false);
            setTkRules(settings.tk_rules || []);
            setShowGroupSettings(true);
        } catch (err) {
            setError('Gagal memuat settings grup');
        } finally {
            setSettingsLoading(false);
        }
    };

    const handleSaveGroupSettings = async () => {
        if (!settingsGroup) return;
        setSettingsLoading(true);
        try {
            // Save operators
            await adminAPI.updateOperators(token, settingsGroup.id, groupOperators);
            // Save TK settings
            await adminAPI.updateTKSettings(token, settingsGroup.id, {
                tk_user_id: tkUserId,
                tk_enabled: tkEnabled,
                tk_rules: tkRules
            });
            setSuccess('Settings berhasil disimpan');
            setShowGroupSettings(false);
            loadData();
        } catch (err) {
            setError(err.response?.data?.detail || 'Gagal menyimpan settings');
        } finally {
            setSettingsLoading(false);
        }
    };

    const toggleOperator = (userId) => {
        setGroupOperators(prev => {
            if (prev.includes(userId)) {
                return prev.filter(id => id !== userId);
            } else {
                return [...prev, userId];
            }
        });
    };

    const addTkRule = () => {
        setTkRules(prev => [...prev, { min_win: 0, fee: 0 }]);
    };

    const updateTkRule = (index, field, value) => {
        setTkRules(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: parseFloat(value) || 0 };
            return updated;
        });
    };

    const removeTkRule = (index) => {
        setTkRules(prev => prev.filter((_, i) => i !== index));
    };

    // Clear messages after 3 seconds
    useEffect(() => {
        if (error || success) {
            const timer = setTimeout(() => {
                setError('');
                setSuccess('');
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [error, success]);

    return (
        <div className="dashboard-layout">
            {/* Mobile menu button */}
            <button 
                className="mobile-menu-btn lg:hidden"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                data-testid="mobile-menu-btn"
            >
                {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>

            {/* Sidebar overlay */}
            <div 
                className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`}
                onClick={() => setSidebarOpen(false)}
            />

            {/* Sidebar */}
            <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
                <div className="flex-shrink-0">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-md bg-neon-green/10 border border-neon-green/30 flex items-center justify-center">
                                <CurrencySymbol size="md" className="text-neon-green" />
                            </div>
                            <div>
                                <h1 className="text-sm font-headings font-bold text-white">VG WALLET</h1>
                                <p className="text-xs text-zinc-500 font-mono">ADMIN</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {/* Fun Mode Toggle */}
                            <FunModeToggle />
                            {/* Theme Toggle */}
                            <button
                                onClick={toggleTheme}
                                className="p-2 rounded-md hover:bg-zinc-800 transition-colors"
                                data-testid="theme-toggle-btn"
                                title={isDark ? 'Mode Terang' : 'Mode Gelap'}
                            >
                                {isDark ? (
                                    <Sun className="w-5 h-5 text-yellow-400" />
                                ) : (
                                    <Moon className="w-5 h-5 text-zinc-600" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Admin Balance Card */}
                    <div className="stat-card-glow mb-6">
                        <p className="text-xs text-zinc-500 font-mono uppercase mb-1">Your Balance</p>
                        <CurrencyDisplay amount={adminBalance} size="lg" className="text-neon-green" />
                    </div>

                    <nav className="space-y-1">
                        <button
                            onClick={() => { setActiveTab('users'); setSidebarOpen(false); }}
                            className={`nav-item w-full text-left ${activeTab === 'users' ? 'active' : ''}`}
                            data-testid="nav-users"
                        >
                            <Users className="w-5 h-5" />
                            <span>Manage Users</span>
                        </button>
                        <button
                            onClick={() => { setActiveTab('groups'); setSidebarOpen(false); }}
                            className={`nav-item w-full text-left ${activeTab === 'groups' ? 'active' : ''}`}
                            data-testid="nav-groups"
                        >
                            <FolderOpen className="w-5 h-5" />
                            <span>Manage Groups</span>
                        </button>
                        <button
                            onClick={() => { setActiveTab('klasemen'); setSidebarOpen(false); }}
                            className={`nav-item w-full text-left ${activeTab === 'klasemen' ? 'active' : ''}`}
                            data-testid="nav-klasemen"
                        >
                            <Trophy className="w-5 h-5" />
                            <span>Klasemen</span>
                        </button>
                        <button
                            onClick={() => { setActiveTab('tournament'); setSidebarOpen(false); }}
                            className={`nav-item w-full text-left ${activeTab === 'tournament' ? 'active' : ''}`}
                            data-testid="nav-tournament"
                        >
                            <Award className="w-5 h-5" />
                            <span>Tournament</span>
                        </button>
                        <button
                            onClick={() => { setActiveTab('transactions'); setSidebarOpen(false); }}
                            className={`nav-item w-full text-left ${activeTab === 'transactions' ? 'active' : ''}`}
                            data-testid="nav-transactions"
                        >
                            <History className="w-5 h-5" />
                            <span>Transactions</span>
                        </button>
                        <button
                            onClick={() => { setActiveTab('h2h'); setSidebarOpen(false); }}
                            className={`nav-item w-full text-left ${activeTab === 'h2h' ? 'active' : ''}`}
                            data-testid="nav-h2h"
                        >
                            <BarChart3 className="w-5 h-5" />
                            <span>Head-to-Head</span>
                        </button>
                        {/* Logout button moved below transactions */}
                        <button
                            onClick={handleLogout}
                            className="nav-item w-full text-left text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            data-testid="logout-btn"
                        >
                            <LogOut className="w-5 h-5" />
                            <span>Logout</span>
                        </button>
                    </nav>
                </div>

                <div className="flex-grow"></div>
                
                <div className="flex-shrink-0 pt-4 border-t border-zinc-800">
                    <div className="px-3 py-2">
                        <p className="text-xs text-zinc-500 font-mono">LOGGED IN AS</p>
                        <p className="text-sm text-white font-medium">{user?.username}</p>
                    </div>
                </div>
            </aside>

            {/* Main content */}
            <main className="main-content">
                {/* Notifications */}
                {error && (
                    <div className="fixed top-4 right-4 z-50 flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/20 rounded-md text-red-400 animate-slide-up">
                        <AlertCircle className="w-4 h-4" />
                        {error}
                    </div>
                )}
                {success && (
                    <div className="fixed top-4 right-4 z-50 flex items-center gap-2 p-4 bg-neon-green/10 border border-neon-green/20 rounded-md text-neon-green animate-slide-up">
                        <CheckCircle className="w-4 h-4" />
                        {success}
                    </div>
                )}

                {/* Users Tab */}
                {activeTab === 'users' && (
                    <div>
                        <div className="page-header flex items-start justify-between">
                            <div>
                                <h1 className="page-title">Manage Users</h1>
                                <p className="page-subtitle">Create, manage, and allocate balance to users</p>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    onClick={loadData}
                                    variant="outline"
                                    size="sm"
                                    className="border-zinc-700 text-zinc-400 hover:text-white"
                                >
                                    <RefreshCw className="w-4 h-4" />
                                </Button>
                                <Button
                                    onClick={() => setShowCreateUser(true)}
                                    className="bg-neon-green hover:bg-neon-green/90 text-void-black"
                                    data-testid="create-user-btn"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Create User
                                </Button>
                            </div>
                        </div>

                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <span className="spinner" />
                            </div>
                        ) : users.length === 0 ? (
                            <div className="empty-state">
                                <Users className="empty-state-icon mx-auto" />
                                <p>No users created yet</p>
                            </div>
                        ) : (
                            <div className="grid-cards">
                                {users.map((u) => (
                                    <div key={u.id} className="stat-card card-hover" data-testid={`user-card-${u.id}`}>
                                        <div className="flex items-start justify-between mb-4">
                                            <div>
                                                <p className="text-sm text-zinc-500 font-mono uppercase">User</p>
                                                <h3 className="text-lg font-headings font-semibold text-white">{u.username}</h3>
                                            </div>
                                            <div className="flex flex-wrap gap-1">
                                                <div className={`badge ${u.requires_setup ? 'badge-warning' : 'badge-success'}`}>
                                                    {u.requires_setup ? 'Pending Setup' : 'Active'}
                                                </div>
                                                {u.is_finance && (
                                                    <div className="badge bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                                                        💰 Finance
                                                    </div>
                                                )}
                                                {u.is_hidden && (
                                                    <div className="badge bg-red-500/20 text-red-400 border-red-500/30">
                                                        👁️‍🗨️ Hidden
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        
                                        <div className="mb-4">
                                            <p className="text-xs text-zinc-500 mb-1">Balance</p>
                                            <CurrencyDisplay amount={u.balance} size="lg" className="text-neon-green" />
                                        </div>

                                        <div className="text-xs text-zinc-600 mb-4">
                                            Groups: {u.groups.length > 0 ? u.groups.map(gId => {
                                                const g = groups.find(gr => gr.id === gId);
                                                return g?.name;
                                            }).filter(Boolean).join(', ') : 'None'}
                                        </div>

                                        <div className="flex flex-wrap gap-2">
                                            <Button
                                                onClick={() => handleToggleFinance(u.id)}
                                                size="sm"
                                                className={`${u.is_finance ? 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 border border-yellow-500/30' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 border border-zinc-700'}`}
                                                data-testid={`toggle-finance-btn-${u.id}`}
                                            >
                                                {u.is_finance ? '💰 ON' : '💰 OFF'}
                                            </Button>
                                            <Button
                                                onClick={() => handleToggleHidden(u.id)}
                                                size="sm"
                                                className={`${u.is_hidden ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 border border-zinc-700'}`}
                                                data-testid={`toggle-hidden-btn-${u.id}`}
                                            >
                                                {u.is_hidden ? '👁️ Hidden' : '👁️ Visible'}
                                            </Button>
                                            <Button
                                                onClick={() => { setSelectedUser(u); setShowAddBalance(true); }}
                                                size="sm"
                                                className="bg-neon-green/10 text-neon-green hover:bg-neon-green/20 border border-neon-green/20"
                                                data-testid={`add-balance-btn-${u.id}`}
                                            >
                                                <CreditCard className="w-3 h-3 mr-1" />
                                                Balance
                                            </Button>
                                            <Button
                                                onClick={() => { setSelectedUser(u); setShowResetPassword(true); }}
                                                size="sm"
                                                variant="outline"
                                                className="border-zinc-700 text-zinc-400 hover:text-white"
                                                data-testid={`reset-password-btn-${u.id}`}
                                            >
                                                <Key className="w-3 h-3" />
                                            </Button>
                                            <Button
                                                onClick={() => { setSelectedUser(u); setShowDeleteUserConfirm(true); }}
                                                size="sm"
                                                variant="outline"
                                                className="border-red-500/20 text-red-400 hover:bg-red-500/10"
                                                data-testid={`delete-user-btn-${u.id}`}
                                            >
                                                <Trash2 className="w-3 h-3" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Groups Tab */}
                {activeTab === 'groups' && (
                    <div>
                        <div className="page-header flex items-start justify-between">
                            <div>
                                <h1 className="page-title">Manage Groups</h1>
                                <p className="page-subtitle">Create groups and add users to them</p>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    onClick={loadData}
                                    variant="outline"
                                    size="sm"
                                    className="border-zinc-700 text-zinc-400 hover:text-white"
                                >
                                    <RefreshCw className="w-4 h-4" />
                                </Button>
                                <Button
                                    onClick={() => setShowCreateGroup(true)}
                                    className="bg-neon-green hover:bg-neon-green/90 text-void-black"
                                    data-testid="create-group-btn"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Create Group
                                </Button>
                            </div>
                        </div>

                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <span className="spinner" />
                            </div>
                        ) : groups.length === 0 ? (
                            <div className="empty-state">
                                <FolderOpen className="empty-state-icon mx-auto" />
                                <p>No groups created yet</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {groups.map((g) => (
                                    <div key={g.id} className="stat-card card-hover" data-testid={`group-card-${g.id}`}>
                                        <div className="flex items-start justify-between mb-4">
                                            <div>
                                                <h3 className="text-xl font-headings font-semibold text-white">{g.name}</h3>
                                                <p className="text-sm text-zinc-500">{g.members.length} members</p>
                                            </div>
                                            
                                            {/* Public Link */}
                                            {g.public_link && (
                                                <div className="mb-3 p-2 bg-zinc-900 rounded border border-zinc-800">
                                                    <p className="text-xs text-zinc-500 mb-1">Link Klasemen Publik:</p>
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="text"
                                                            value={g.public_link}
                                                            readOnly
                                                            className="flex-1 text-xs bg-transparent text-neon-green font-mono border-0 outline-none"
                                                        />
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={async () => {
                                                                try {
                                                                    await navigator.clipboard.writeText(g.public_link);
                                                                    setSuccess('Link copied!');
                                                                } catch (err) {
                                                                    // Fallback for browsers that don't support clipboard API
                                                                    const textArea = document.createElement('textarea');
                                                                    textArea.value = g.public_link;
                                                                    textArea.style.position = 'fixed';
                                                                    textArea.style.left = '-9999px';
                                                                    document.body.appendChild(textArea);
                                                                    textArea.select();
                                                                    try {
                                                                        document.execCommand('copy');
                                                                        setSuccess('Link copied!');
                                                                    } catch (e) {
                                                                        setError('Gagal menyalin link. Silakan copy manual.');
                                                                    }
                                                                    document.body.removeChild(textArea);
                                                                }
                                                            }}
                                                            className="text-zinc-400 hover:text-white h-6 px-2"
                                                        >
                                                            <Copy className="w-3 h-3" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {/* Public Leaderboard Toggle */}
                                            <div className="mb-3 flex items-center justify-between p-2 bg-zinc-900 rounded border border-zinc-800">
                                                <div>
                                                    <p className="text-sm text-white">Klasemen Publik</p>
                                                    <p className="text-xs text-zinc-500">Izinkan siapapun melihat klasemen tanpa login</p>
                                                </div>
                                                <Button
                                                    size="sm"
                                                    onClick={() => handleToggleGroupPublic(g.id)}
                                                    className={`${g.is_public 
                                                        ? 'bg-neon-green/20 text-neon-green border-neon-green/30' 
                                                        : 'bg-zinc-800 text-zinc-400 border-zinc-700'} border`}
                                                    data-testid={`toggle-public-${g.id}`}
                                                >
                                                    {g.is_public ? 'ON' : 'OFF'}
                                                </Button>
                                            </div>
                                            
                                            {/* Hide Zero Scores Toggle */}
                                            <div className="mb-3 flex items-center justify-between p-2 bg-zinc-900 rounded border border-zinc-800">
                                                <div>
                                                    <p className="text-sm text-white">Hide Score 0</p>
                                                    <p className="text-xs text-zinc-500">Sembunyikan user dengan score 0 di klasemen</p>
                                                </div>
                                                <Button
                                                    size="sm"
                                                    onClick={() => handleToggleHideZeroScores(g.id)}
                                                    className={`${g.hide_zero_scores 
                                                        ? 'bg-orange-500/20 text-orange-400 border-orange-500/30' 
                                                        : 'bg-zinc-800 text-zinc-400 border-zinc-700'} border`}
                                                    data-testid={`toggle-hide-zero-${g.id}`}
                                                >
                                                    {g.hide_zero_scores ? 'ON' : 'OFF'}
                                                </Button>
                                            </div>
                                            
                                            <div className="flex gap-2 flex-wrap">
                                                <Button
                                                    onClick={() => handleOpenKlasmen(g)}
                                                    size="sm"
                                                    className="bg-yellow-500/10 text-yellow-500 hover:bg-yellow-500/20 border border-yellow-500/20"
                                                    data-testid={`view-klasemen-btn-${g.id}`}
                                                >
                                                    <Trophy className="w-4 h-4 mr-1" />
                                                    Klasemen
                                                </Button>
                                                <Button
                                                    onClick={() => handleOpenStatistics(g)}
                                                    size="sm"
                                                    className="bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 border border-purple-500/20"
                                                    data-testid={`view-statistics-btn-${g.id}`}
                                                >
                                                    <BarChart3 className="w-4 h-4 mr-1" />
                                                    Statistik
                                                </Button>
                                                <Button
                                                    onClick={() => handleCheckDuplicates(g)}
                                                    size="sm"
                                                    variant="outline"
                                                    className="border-orange-500/20 text-orange-500 hover:bg-orange-500/10"
                                                    data-testid={`check-duplicates-btn-${g.id}`}
                                                >
                                                    <Copy className="w-4 h-4 mr-1" />
                                                    Cek Duplicate
                                                </Button>
                                                <Button
                                                    onClick={() => handleOpenGroupSettings(g)}
                                                    size="sm"
                                                    className="bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 border border-blue-500/20"
                                                    data-testid={`group-settings-btn-${g.id}`}
                                                >
                                                    <Settings className="w-4 h-4 mr-1" />
                                                    Settings
                                                </Button>
                                                <Button
                                                    onClick={() => { setSelectedGroup(g); setShowAddToGroup(true); }}
                                                    size="sm"
                                                    className="bg-neon-green/10 text-neon-green hover:bg-neon-green/20 border border-neon-green/20"
                                                    data-testid={`add-to-group-btn-${g.id}`}
                                                >
                                                    <UserPlus className="w-4 h-4 mr-1" />
                                                    Add User
                                                </Button>
                                                <Button
                                                    onClick={() => { setSelectedGroup(g); setShowDeleteGroupConfirm(true); }}
                                                    size="sm"
                                                    variant="outline"
                                                    className="border-red-500/20 text-red-400 hover:bg-red-500/10"
                                                    data-testid={`delete-group-btn-${g.id}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                        
                                        {g.members.length > 0 && (
                                            <div className="border-t border-zinc-800 pt-4">
                                                <p className="text-xs text-zinc-500 font-mono uppercase mb-3">Members</p>
                                                <div className="flex flex-wrap gap-2">
                                                    {g.members.map(memberId => (
                                                        <div key={memberId} className="flex items-center gap-2 px-3 py-1.5 bg-zinc-800 rounded-md">
                                                            <span className="text-sm text-zinc-300">{getUsernameById(memberId)}</span>
                                                            <button
                                                                onClick={() => handleRemoveFromGroup(g.id, memberId)}
                                                                className="text-red-400 hover:text-red-300"
                                                                data-testid={`remove-member-${memberId}`}
                                                            >
                                                                <UserMinus className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Tournament Tab */}
                {activeTab === 'tournament' && (
                    <TournamentManager 
                        token={token} 
                        users={users}
                        onError={setError}
                        onSuccess={setSuccess}
                    />
                )}

                {/* Transactions Tab */}
                {activeTab === 'transactions' && (
                    <div>
                        <div className="page-header flex items-start justify-between">
                            <div>
                                <h1 className="page-title">Transactions</h1>
                                <p className="page-subtitle">View and export transaction history</p>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    onClick={loadData}
                                    variant="outline"
                                    size="sm"
                                    className="border-zinc-700 text-zinc-400 hover:text-white"
                                >
                                    <RefreshCw className="w-4 h-4" />
                                </Button>
                                <Button
                                    onClick={handleExportCSV}
                                    variant="outline"
                                    className="border-zinc-700 text-zinc-400 hover:text-white"
                                    data-testid="export-csv-btn"
                                >
                                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                                    CSV
                                </Button>
                                <Button
                                    onClick={handleExportPDF}
                                    variant="outline"
                                    className="border-zinc-700 text-zinc-400 hover:text-white"
                                    data-testid="export-pdf-btn"
                                >
                                    <FileText className="w-4 h-4 mr-2" />
                                    PDF
                                </Button>
                            </div>
                        </div>

                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <span className="spinner" />
                            </div>
                        ) : transactions.length === 0 ? (
                            <div className="empty-state">
                                <History className="empty-state-icon mx-auto" />
                                <p>No transactions yet</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto rounded-lg border border-zinc-800">
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Time</th>
                                            <th>From</th>
                                            <th>To</th>
                                            <th>Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {transactions.map((tx) => (
                                            <tr key={tx.id} data-testid={`transaction-row-${tx.id}`}>
                                                <td>{formatDate(tx.created_at)}</td>
                                                <td>{formatTime(tx.created_at)}</td>
                                                <td className="text-zinc-300">{tx.from_username}</td>
                                                <td className="text-zinc-300">{tx.to_username}</td>
                                                <td>
                                                    <CurrencyDisplay amount={tx.amount} size="sm" className="text-neon-green" />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* Klasemen Tab */}
                {activeTab === 'klasemen' && (
                    <div className="overflow-x-hidden">
                        <div className="page-header">
                            <h1 className="page-title">Klasemen</h1>
                            <p className="page-subtitle">Leaderboard dan Score Cards per grup</p>
                        </div>

                        {/* Group Selection */}
                        <div className="stat-card mb-4">
                            <h3 className="text-sm sm:text-base font-semibold text-white mb-3">Pilih Grup</h3>
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
                                {groups.map(group => (
                                    <button
                                        key={group.id}
                                        onClick={() => {
                                            setKlasmenTabGroup(group);
                                            loadKlasmenData(group.id, klasmenMonth, klasmenYear);
                                        }}
                                        className={`p-2 sm:p-3 rounded-lg border text-left transition-all ${
                                            klasmenTabGroup?.id === group.id
                                                ? 'bg-neon-green/20 border-neon-green text-neon-green'
                                                : 'bg-zinc-800/50 border-zinc-700 text-zinc-300 hover:border-zinc-600'
                                        }`}
                                    >
                                        <div className="font-medium text-sm truncate">{group.name}</div>
                                        <div className="text-xs text-zinc-500">{group.members?.length || 0} anggota</div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Month/Year Filter */}
                        {klasmenTabGroup && (
                            <div className="stat-card mb-4">
                                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                                    <select
                                        value={klasmenMonth}
                                        onChange={(e) => {
                                            const month = parseInt(e.target.value);
                                            setKlasmenMonth(month);
                                            loadKlasmenData(klasmenTabGroup.id, month, klasmenYear);
                                        }}
                                        className="bg-zinc-800 border border-zinc-700 rounded px-2 sm:px-3 py-1.5 text-sm text-white"
                                    >
                                        {[...Array(12)].map((_, i) => (
                                            <option key={i+1} value={i+1}>
                                                {new Date(2000, i).toLocaleString('id-ID', { month: 'long' })}
                                            </option>
                                        ))}
                                    </select>
                                    <select
                                        value={klasmenYear}
                                        onChange={(e) => {
                                            const year = parseInt(e.target.value);
                                            setKlasmenYear(year);
                                            loadKlasmenData(klasmenTabGroup.id, klasmenMonth, year);
                                        }}
                                        className="bg-zinc-800 border border-zinc-700 rounded px-2 sm:px-3 py-1.5 text-sm text-white"
                                    >
                                        {[2024, 2025, 2026, 2027].map(y => (
                                            <option key={y} value={y}>{y}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        )}

                        {/* Loading */}
                        {klasmenLoading && (
                            <div className="text-center py-8">
                                <div className="animate-spin w-8 h-8 border-2 border-neon-green border-t-transparent rounded-full mx-auto"></div>
                                <p className="text-zinc-500 mt-2">Memuat data...</p>
                            </div>
                        )}

                        {/* Leaderboard */}
                        {klasmenTabGroup && klasmenData && !klasmenLoading && (
                            <div className="space-y-4">
                                {/* Leaderboard Table */}
                                <div className="stat-card">
                                    <h3 className="text-sm sm:text-base font-semibold text-white mb-3">
                                        Leaderboard - {klasmenTabGroup.name}
                                    </h3>
                                    {klasmenData.leaderboard?.entries?.length > 0 ? (
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm">
                                                <thead>
                                                    <tr className="text-zinc-500 border-b border-zinc-700 text-xs">
                                                        <th className="py-2 text-left">#</th>
                                                        <th className="py-2 text-left">Saldo</th>
                                                        <th className="py-2 text-left">Nama</th>
                                                        <th className="py-2 text-right">Score</th>
                                                        <th className="py-2 text-right">Games</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {klasmenData.leaderboard.entries.map((entry, idx) => (
                                                        <tr key={entry.user_id} className="border-b border-zinc-800">
                                                            <td className="py-2 text-zinc-400">{idx + 1}</td>
                                                            <td className={`py-2 font-mono text-xs ${
                                                                (entry.balance || 0) > 0 ? 'text-neon-green' : 
                                                                (entry.balance || 0) < 0 ? 'text-red-400' : 'text-zinc-400'
                                                            }`}>
                                                                {formatNumber(entry.balance || 0)}
                                                            </td>
                                                            <td className="py-2 text-white font-medium uppercase text-xs">{entry.username}</td>
                                                            <td className={`py-2 text-right font-mono ${
                                                                entry.total_score > 0 ? 'text-neon-green' : 
                                                                entry.total_score < 0 ? 'text-red-400' : 'text-zinc-400'
                                                            }`}>
                                                                {entry.total_score > 0 ? '+' : ''}{formatNumber(entry.total_score)}
                                                            </td>
                                                            <td className="py-2 text-right text-zinc-400">{entry.score_count}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <p className="text-zinc-500 text-sm">Belum ada data untuk bulan ini</p>
                                    )}
                                </div>

                                {/* Score Cards */}
                                {klasmenData.scorecards?.length > 0 && (
                                    <div className="stat-card">
                                        <h3 className="text-sm sm:text-base font-semibold text-white mb-3">
                                            Score Cards ({klasmenData.scorecards.length})
                                        </h3>
                                        <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 pt-2 pl-2">
                                            {klasmenData.scorecards.map((sc, idx) => (
                                                <div 
                                                    key={sc.id} 
                                                    className="stat-card relative border-zinc-700"
                                                >
                                                    {/* Card Number */}
                                                    <div className="absolute -top-2 -left-2 w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-neon-green flex items-center justify-center z-10">
                                                        <span className="text-[10px] sm:text-xs font-bold text-void-black">#{klasmenData.scorecards.length - idx}</span>
                                                    </div>
                                                    
                                                    {/* Header */}
                                                    <div className="pl-4 mb-2">
                                                        <p className="text-[10px] text-zinc-500 font-mono">
                                                            {new Date(sc.created_at).toLocaleDateString('id-ID')}
                                                        </p>
                                                        <p className="text-[10px] text-zinc-600">
                                                            by {sc.created_by_username?.toUpperCase()}
                                                        </p>
                                                    </div>
                                                    
                                                    {/* Entries */}
                                                    <div className="space-y-1">
                                                        {sc.entries?.map((entry, eIdx) => (
                                                            <div key={eIdx} className="flex justify-between items-center py-0.5 px-1">
                                                                <span className="text-xs font-bold uppercase text-white truncate">{entry.username}</span>
                                                                <span className={`text-xs font-mono font-bold ${
                                                                    entry.score > 0 ? 'text-neon-green' : 
                                                                    entry.score < 0 ? 'text-red-400' : 'text-zinc-400'
                                                                }`}>
                                                                    {entry.score > 0 ? '+' : ''}{formatNumber(entry.score)}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* No group selected */}
                        {!klasmenTabGroup && (
                            <div className="text-center py-8 text-zinc-500">
                                Pilih grup untuk melihat klasemen
                            </div>
                        )}
                    </div>
                )}

                {/* Head-to-Head Statistics Tab */}
                {activeTab === 'h2h' && (
                    <div>
                        <div className="page-header">
                            <h1 className="page-title">Head-to-Head Statistics</h1>
                            <p className="page-subtitle">Statistik ketika pemain bermain bersama</p>
                        </div>

                        {/* Group Selection */}
                        <div className="stat-card mb-6">
                            <h3 className="text-lg font-semibold text-white mb-4">1. Pilih Grup</h3>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                {groups.map(group => (
                                    <button
                                        key={group.id}
                                        onClick={() => {
                                            setH2hSelectedGroup(group);
                                            setH2hSelectedPlayers([]);
                                            setH2hData(null);
                                        }}
                                        className={`p-3 rounded-lg border text-left transition-all ${
                                            h2hSelectedGroup?.id === group.id
                                                ? 'bg-neon-green/20 border-neon-green text-neon-green'
                                                : 'bg-zinc-800/50 border-zinc-700 text-zinc-300 hover:border-zinc-600'
                                        }`}
                                    >
                                        <div className="font-medium">{group.name}</div>
                                        <div className="text-xs text-zinc-500">{group.members?.length || 0} anggota</div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Player Selection */}
                        {h2hSelectedGroup && (
                            <div className="stat-card mb-6">
                                <h3 className="text-lg font-semibold text-white mb-4">
                                    2. Pilih Pemain ({h2hSelectedPlayers.length} dipilih)
                                </h3>
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
                                    {users
                                        .filter(u => h2hSelectedGroup.members?.includes(u.id))
                                        .map(u => (
                                            <button
                                                key={u.id}
                                                onClick={() => toggleH2HPlayer(u.id)}
                                                className={`p-3 rounded-lg border text-left transition-all ${
                                                    h2hSelectedPlayers.includes(u.id)
                                                        ? 'bg-neon-green/20 border-neon-green text-neon-green'
                                                        : 'bg-zinc-800/50 border-zinc-700 text-zinc-300 hover:border-zinc-600'
                                                }`}
                                            >
                                                <div className="flex items-center gap-2">
                                                    {h2hSelectedPlayers.includes(u.id) && (
                                                        <CheckCircle className="w-4 h-4" />
                                                    )}
                                                    <span className="font-medium uppercase">{u.username}</span>
                                                </div>
                                            </button>
                                        ))}
                                </div>
                                <Button
                                    onClick={loadH2HStatistics}
                                    disabled={h2hSelectedPlayers.length < 2 || h2hLoading}
                                    className="bg-neon-green hover:bg-neon-green/90 text-void-black"
                                >
                                    {h2hLoading ? (
                                        <span className="flex items-center gap-2">
                                            <span className="spinner w-4 h-4" />
                                            Loading...
                                        </span>
                                    ) : (
                                        <>
                                            <BarChart3 className="w-4 h-4 mr-2" />
                                            Lihat Statistik
                                        </>
                                    )}
                                </Button>
                            </div>
                        )}

                        {/* H2H Results */}
                        {h2hData && (
                            <div className="space-y-6">
                                {/* Summary */}
                                <div className="stat-card">
                                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                        <Trophy className="w-5 h-5 text-yellow-500" />
                                        Statistik Main Bersama
                                    </h3>
                                    <p className="text-zinc-400 mb-4">
                                        Total <span className="text-neon-green font-bold">{h2hData.total_games_together}</span> game bermain bersama
                                    </p>
                                    
                                    {h2hData.total_games_together === 0 ? (
                                        <div className="text-center py-8 text-zinc-500">
                                            <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                            <p>Pemain yang dipilih belum pernah bermain bersama</p>
                                        </div>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="data-table w-full">
                                                <thead>
                                                    <tr>
                                                        <th className="text-left">Pemain</th>
                                                        <th className="text-center">Menang</th>
                                                        <th className="text-center">Win Rate</th>
                                                        <th className="text-center">Total Skor</th>
                                                        <th className="text-center">Avg Skor</th>
                                                        <th className="text-center">Avg Rank</th>
                                                        <th className="text-center">Best</th>
                                                        <th className="text-center">Worst</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {h2hData.statistics.map((stat, idx) => (
                                                        <tr key={stat.user_id} className={idx === 0 ? 'bg-yellow-500/10' : ''}>
                                                            <td className="font-medium">
                                                                <div className="flex items-center gap-2">
                                                                    {idx === 0 && <Trophy className="w-4 h-4 text-yellow-500" />}
                                                                    <span className="uppercase">{stat.username}</span>
                                                                </div>
                                                            </td>
                                                            <td className="text-center font-bold text-neon-green">{stat.wins}</td>
                                                            <td className="text-center">
                                                                <span className={stat.win_rate >= 50 ? 'text-neon-green' : 'text-zinc-400'}>
                                                                    {stat.win_rate}%
                                                                </span>
                                                            </td>
                                                            <td className={`text-center font-medium ${getScoreColorClass(stat.total_score)}`}>
                                                                {stat.total_score > 0 ? '+' : ''}{stat.total_score}
                                                            </td>
                                                            <td className={`text-center ${getScoreColorClass(stat.average_score)}`}>
                                                                {stat.average_score > 0 ? '+' : ''}{stat.average_score}
                                                            </td>
                                                            <td className="text-center text-zinc-400">#{stat.average_rank}</td>
                                                            <td className="text-center text-neon-green">
                                                                {stat.best_score !== null ? (stat.best_score > 0 ? '+' : '') + stat.best_score : '-'}
                                                            </td>
                                                            <td className="text-center text-red-500">
                                                                {stat.worst_score !== null ? (stat.worst_score > 0 ? '+' : '') + stat.worst_score : '-'}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>

                                {/* Head-to-Head Pairs */}
                                {h2hData.head_to_head_pairs && h2hData.head_to_head_pairs.length > 0 && h2hData.total_games_together > 0 && (
                                    <div className="stat-card">
                                        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                            <Users className="w-5 h-5 text-blue-400" />
                                            Head-to-Head Record
                                        </h3>
                                        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                                            {h2hData.head_to_head_pairs.map((pair, idx) => (
                                                <div key={idx} className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700">
                                                    <div className="flex items-center justify-between mb-3">
                                                        <div className="text-center flex-1">
                                                            <div className="text-xs text-zinc-500 uppercase mb-1">Player 1</div>
                                                            <div className="font-bold text-white uppercase">{pair.player1.username}</div>
                                                            <div className={`text-2xl font-bold ${pair.player1.wins > pair.player2.wins ? 'text-neon-green' : 'text-zinc-400'}`}>
                                                                {pair.player1.wins}
                                                            </div>
                                                        </div>
                                                        <div className="px-3 text-zinc-500 text-sm">
                                                            VS
                                                            {pair.ties > 0 && (
                                                                <div className="text-xs text-zinc-600">Seri: {pair.ties}</div>
                                                            )}
                                                        </div>
                                                        <div className="text-center flex-1">
                                                            <div className="text-xs text-zinc-500 uppercase mb-1">Player 2</div>
                                                            <div className="font-bold text-white uppercase">{pair.player2.username}</div>
                                                            <div className={`text-2xl font-bold ${pair.player2.wins > pair.player1.wins ? 'text-neon-green' : 'text-zinc-400'}`}>
                                                                {pair.player2.wins}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="text-center text-xs text-zinc-500">
                                                        {pair.total_games} game
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </main>

            {/* Group Settings Modal (Operator & TK) */}
            <Dialog open={showGroupSettings} onOpenChange={setShowGroupSettings}>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-headings flex items-center gap-2">
                            <Settings className="w-5 h-5" />
                            Settings - {settingsGroup?.name}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {settingsLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <span className="spinner" />
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {/* Operators Section */}
                            <div className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                                    <Key className="w-4 h-4 text-blue-400" />
                                    Operator
                                </h3>
                                <p className="text-xs text-zinc-500 mb-3">
                                    Operator dapat mengelola settlement dan saldo member dalam grup ini.
                                </p>
                                <div className="grid grid-cols-2 gap-2">
                                    {settingsGroup?.members?.map(memberId => (
                                        <button
                                            key={memberId}
                                            onClick={() => toggleOperator(memberId)}
                                            className={`p-2 rounded border text-left text-sm transition-all ${
                                                groupOperators.includes(memberId)
                                                    ? 'bg-blue-500/20 border-blue-500 text-blue-400'
                                                    : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-zinc-600'
                                            }`}
                                        >
                                            <div className="flex items-center gap-2">
                                                {groupOperators.includes(memberId) && <CheckCircle className="w-3 h-3" />}
                                                <span className="uppercase">{getUsernameById(memberId)}</span>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* TK (Tabungan Kas) Section */}
                            <div className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                                    <PiggyBank className="w-4 h-4 text-yellow-500" />
                                    TK (Tabungan Kas)
                                </h3>
                                <p className="text-xs text-zinc-500 mb-3">
                                    Pilih member sebagai TK. Auto-potong dari pemenang tertinggi sesuai aturan.
                                </p>
                                
                                {/* TK Enable Toggle */}
                                <div className="flex items-center justify-between mb-4 p-2 bg-zinc-900 rounded">
                                    <span className="text-sm text-zinc-300">Aktifkan TK</span>
                                    <Button
                                        size="sm"
                                        onClick={() => setTkEnabled(!tkEnabled)}
                                        className={`${tkEnabled 
                                            ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' 
                                            : 'bg-zinc-800 text-zinc-400 border-zinc-700'} border`}
                                    >
                                        {tkEnabled ? 'ON' : 'OFF'}
                                    </Button>
                                </div>

                                {/* TK User Selection */}
                                <div className="mb-4">
                                    <Label className="text-xs text-zinc-500 mb-2 block">Pilih Member sebagai TK:</Label>
                                    <div className="grid grid-cols-2 gap-2">
                                        {settingsGroup?.members?.map(memberId => (
                                            <button
                                                key={memberId}
                                                onClick={() => setTkUserId(tkUserId === memberId ? null : memberId)}
                                                className={`p-2 rounded border text-left text-sm transition-all ${
                                                    tkUserId === memberId
                                                        ? 'bg-yellow-500/20 border-yellow-500 text-yellow-400'
                                                        : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-zinc-600'
                                                }`}
                                            >
                                                <div className="flex items-center gap-2">
                                                    {tkUserId === memberId && <PiggyBank className="w-3 h-3" />}
                                                    <span className="uppercase">{getUsernameById(memberId)}</span>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* TK Rules */}
                                {tkEnabled && (
                                    <div>
                                        <Label className="text-xs text-zinc-500 mb-2 block">Aturan Potongan TK:</Label>
                                        <div className="space-y-2 mb-3">
                                            {tkRules.map((rule, idx) => (
                                                <div key={idx} className="flex items-center gap-2 p-2 bg-zinc-900 rounded">
                                                    <span className="text-xs text-zinc-500">Win ≥</span>
                                                    <Input
                                                        type="text"
                                                        inputMode="decimal"
                                                        pattern="[0-9]*[.,]?[0-9]*"
                                                        value={rule.min_win}
                                                        onChange={(e) => updateTkRule(idx, 'min_win', e.target.value.replace(',', '.'))}
                                                        className="w-20 h-8 bg-zinc-800 border-zinc-700 text-sm text-[16px]"
                                                        placeholder="15"
                                                    />
                                                    <span className="text-xs text-zinc-500">→ TK +</span>
                                                    <Input
                                                        type="text"
                                                        inputMode="decimal"
                                                        pattern="[0-9]*[.,]?[0-9]*"
                                                        value={rule.fee}
                                                        onChange={(e) => updateTkRule(idx, 'fee', e.target.value.replace(',', '.'))}
                                                        className="w-20 h-8 bg-zinc-800 border-zinc-700 text-sm text-[16px]"
                                                        placeholder="0.5"
                                                    />
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => removeTkRule(idx)}
                                                        className="text-red-400 hover:text-red-300 h-8 px-2"
                                                    >
                                                        <Trash2 className="w-3 h-3" />
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={addTkRule}
                                            className="border-zinc-700 text-zinc-400"
                                        >
                                            <Plus className="w-3 h-3 mr-1" />
                                            Tambah Aturan
                                        </Button>
                                    </div>
                                )}
                            </div>

                            {/* Save Button */}
                            <div className="flex justify-end gap-2">
                                <Button
                                    variant="outline"
                                    onClick={() => setShowGroupSettings(false)}
                                    className="border-zinc-700"
                                >
                                    Batal
                                </Button>
                                <Button
                                    onClick={handleSaveGroupSettings}
                                    disabled={settingsLoading}
                                    className="bg-neon-green hover:bg-neon-green/90 text-void-black"
                                >
                                    {settingsLoading ? 'Menyimpan...' : 'Simpan Settings'}
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Create User Modal */}
            <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
                <DialogContent className="bg-zinc-900 border-zinc-800">
                    <DialogHeader>
                        <DialogTitle className="font-headings">Create New User</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateUser} className="space-y-4">
                        <div>
                            <Label htmlFor="userUsername" className="form-label">Username</Label>
                            <Input
                                id="userUsername"
                                value={newUsername}
                                onChange={(e) => setNewUsername(e.target.value)}
                                placeholder="Enter username"
                                className="bg-zinc-800 border-zinc-700"
                                data-testid="create-user-username"
                                required
                            />
                        </div>
                        <div>
                            <Label htmlFor="userPassword" className="form-label">Password</Label>
                            <Input
                                id="userPassword"
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Enter password"
                                className="bg-zinc-800 border-zinc-700"
                                data-testid="create-user-password"
                                required
                            />
                        </div>
                        <div className="flex gap-2 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowCreateUser(false)}
                                className="flex-1 border-zinc-700"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                className="flex-1 bg-neon-green hover:bg-neon-green/90 text-void-black"
                                data-testid="create-user-submit"
                            >
                                Create User
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Create Group Modal */}
            <Dialog open={showCreateGroup} onOpenChange={setShowCreateGroup}>
                <DialogContent className="bg-zinc-900 border-zinc-800">
                    <DialogHeader>
                        <DialogTitle className="font-headings">Create New Group</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateGroup} className="space-y-4">
                        <div>
                            <Label htmlFor="groupName" className="form-label">Group Name</Label>
                            <Input
                                id="groupName"
                                value={newGroupName}
                                onChange={(e) => setNewGroupName(e.target.value)}
                                placeholder="Enter group name"
                                className="bg-zinc-800 border-zinc-700"
                                data-testid="create-group-name"
                                required
                            />
                        </div>
                        <div className="flex gap-2 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowCreateGroup(false)}
                                className="flex-1 border-zinc-700"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                className="flex-1 bg-neon-green hover:bg-neon-green/90 text-void-black"
                                data-testid="create-group-submit"
                            >
                                Create Group
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Reset Password Modal */}
            <Dialog open={showResetPassword} onOpenChange={setShowResetPassword}>
                <DialogContent className="bg-zinc-900 border-zinc-800">
                    <DialogHeader>
                        <DialogTitle className="font-headings">Reset Password</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleResetPassword} className="space-y-4">
                        <p className="text-sm text-zinc-400">
                            Reset password for user: <span className="text-white font-medium">{selectedUser?.username}</span>
                        </p>
                        <p className="text-xs text-yellow-500">
                            Note: User will need to setup their account again after password reset.
                        </p>
                        <div>
                            <Label htmlFor="resetPwd" className="form-label">New Password</Label>
                            <Input
                                id="resetPwd"
                                type="password"
                                value={resetPassword}
                                onChange={(e) => setResetPasswordValue(e.target.value)}
                                placeholder="Enter new password"
                                className="bg-zinc-800 border-zinc-700"
                                data-testid="reset-password-input"
                                required
                            />
                        </div>
                        <div className="flex gap-2 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowResetPassword(false)}
                                className="flex-1 border-zinc-700"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                className="flex-1 bg-neon-green hover:bg-neon-green/90 text-void-black"
                                data-testid="reset-password-submit"
                            >
                                Reset Password
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Add Balance Modal */}
            <Dialog open={showAddBalance} onOpenChange={setShowAddBalance}>
                <DialogContent className="bg-zinc-900 border-zinc-800">
                    <DialogHeader>
                        <DialogTitle className="font-headings">Add Balance</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddBalance} className="space-y-4">
                        <p className="text-sm text-zinc-400">
                            Add balance to user: <span className="text-white font-medium">{selectedUser?.username}</span>
                        </p>
                        <p className="text-sm text-zinc-500">
                            Your balance: <CurrencyDisplay amount={adminBalance} size="sm" className="text-neon-green" />
                        </p>
                        <p className="text-sm text-zinc-500">
                            User balance: <CurrencyDisplay amount={selectedUser?.balance || 0} size="sm" className="text-zinc-300" />
                        </p>
                        <div>
                            <Label htmlFor="balanceAmt" className="form-label">Amount</Label>
                            <div className="relative">
                                <div className="absolute left-3 top-1/2 -translate-y-1/2">
                                    <CurrencySymbol size="sm" className="text-zinc-500" />
                                </div>
                                <Input
                                    id="balanceAmt"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max={adminBalance}
                                    value={balanceAmount}
                                    onChange={(e) => setBalanceAmount(e.target.value)}
                                    placeholder="0.00"
                                    className="bg-zinc-800 border-zinc-700 pl-8"
                                    data-testid="add-balance-input"
                                    required
                                />
                            </div>
                        </div>
                        <div className="flex gap-2 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowAddBalance(false)}
                                className="flex-1 border-zinc-700"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                className="flex-1 bg-neon-green hover:bg-neon-green/90 text-void-black"
                                data-testid="add-balance-submit"
                            >
                                Add Balance
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Add User to Group Modal */}
            <Dialog open={showAddToGroup} onOpenChange={(open) => { setShowAddToGroup(open); if (!open) setSelectedUserId(''); }}>
                <DialogContent className="bg-zinc-900 border-zinc-800">
                    <DialogHeader>
                        <DialogTitle className="font-headings">Add User to Group</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddToGroup} className="space-y-4">
                        <p className="text-sm text-zinc-400">
                            Add user to group: <span className="text-white font-medium">{selectedGroup?.name}</span>
                        </p>
                        <div>
                            <Label htmlFor="selectUser" className="form-label">Select User</Label>
                            <Select value={selectedUserId} onValueChange={(value) => setSelectedUserId(value)}>
                                <SelectTrigger className="bg-zinc-800 border-zinc-700 w-full" data-testid="select-user-trigger">
                                    <SelectValue placeholder="Select a user" />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-800 border-zinc-700 z-[100]">
                                    {selectedGroup && getUsersNotInGroup(selectedGroup).length > 0 ? (
                                        getUsersNotInGroup(selectedGroup).map(u => (
                                            <SelectItem key={u.id} value={u.id} data-testid={`select-user-${u.id}`}>
                                                {u.username}
                                            </SelectItem>
                                        ))
                                    ) : (
                                        <div className="px-2 py-1 text-sm text-zinc-500">No users available</div>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex gap-2 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowAddToGroup(false)}
                                className="flex-1 border-zinc-700"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={!selectedUserId}
                                className="flex-1 bg-neon-green hover:bg-neon-green/90 text-void-black"
                                data-testid="add-to-group-submit"
                            >
                                Add to Group
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete User Confirmation */}
            <AlertDialog open={showDeleteUserConfirm} onOpenChange={setShowDeleteUserConfirm}>
                <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-headings">Delete User</AlertDialogTitle>
                        <AlertDialogDescription className="text-zinc-400">
                            Are you sure you want to delete user <span className="text-white font-medium">{selectedUser?.username}</span>?
                            This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="border-zinc-700">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteUser}
                            className="bg-red-500 hover:bg-red-600"
                            data-testid="confirm-delete-user-btn"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Delete Group Confirmation */}
            <AlertDialog open={showDeleteGroupConfirm} onOpenChange={setShowDeleteGroupConfirm}>
                <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-headings">Delete Group</AlertDialogTitle>
                        <AlertDialogDescription className="text-zinc-400">
                            Are you sure you want to delete group <span className="text-white font-medium">{selectedGroup?.name}</span>?
                            All members will be removed from this group.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="border-zinc-700">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteGroup}
                            className="bg-red-500 hover:bg-red-600"
                            data-testid="confirm-delete-group-btn"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Duplicate Score Cards Modal */}
            <Dialog open={showDuplicates} onOpenChange={setShowDuplicates}>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-headings flex items-center gap-2">
                            <Copy className="w-5 h-5 text-yellow-500" />
                            Duplicate Score Cards - {selectedGroupForDuplicates?.name}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {duplicateLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <span className="spinner" />
                        </div>
                    ) : duplicateData ? (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between p-3 bg-zinc-800 rounded-md">
                                <div>
                                    <p className="text-sm text-zinc-400">Total Score Cards</p>
                                    <p className="text-xl font-bold text-white">{duplicateData.total_scorecards}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-zinc-400">Duplicates Ditemukan</p>
                                    <p className={`text-xl font-bold ${duplicateData.duplicate_count > 0 ? 'text-red-400' : 'text-neon-green'}`}>
                                        {duplicateData.duplicate_count}
                                    </p>
                                </div>
                            </div>

                            {duplicateData.duplicate_count > 0 ? (
                                <>
                                    <div className="space-y-3">
                                        <p className="text-sm text-zinc-400">Score cards berikut adalah duplicate dan akan dihapus:</p>
                                        {duplicateData.duplicates.map((dup, idx) => (
                                            <div key={idx} className="p-3 bg-red-500/10 border border-red-500/20 rounded-md">
                                                <div className="flex justify-between items-start mb-2">
                                                    <div>
                                                        <p className="text-sm font-bold text-red-400">
                                                            Duplicate oleh: {dup.created_by_username.toUpperCase()}
                                                        </p>
                                                        <p className="text-xs text-zinc-500">
                                                            Original oleh: {dup.original_created_by.toUpperCase()}
                                                        </p>
                                                    </div>
                                                    <span className="text-xs text-zinc-500 font-mono">
                                                        {new Date(dup.created_at).toLocaleString('id-ID')}
                                                    </span>
                                                </div>
                                                <div className="flex flex-wrap gap-2">
                                                    {dup.entries.map((e, i) => (
                                                        <span key={i} className="text-xs px-2 py-1 bg-zinc-800 rounded">
                                                            {e.username.toUpperCase()}: {e.score > 0 ? '+' : ''}{e.score}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="flex gap-2 pt-4 border-t border-zinc-800">
                                        <Button
                                            variant="outline"
                                            onClick={() => setShowDuplicates(false)}
                                            className="flex-1 border-zinc-700"
                                        >
                                            Batal
                                        </Button>
                                        <Button
                                            onClick={handleDeleteDuplicates}
                                            disabled={duplicateLoading}
                                            className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                                            data-testid="delete-duplicates-btn"
                                        >
                                            {duplicateLoading ? (
                                                <span className="flex items-center gap-2">
                                                    <span className="spinner w-4 h-4" />
                                                    Menghapus...
                                                </span>
                                            ) : (
                                                <>
                                                    <Trash2 className="w-4 h-4 mr-2" />
                                                    Hapus {duplicateData.duplicate_count} Duplicate
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </>
                            ) : (
                                <div className="text-center py-6">
                                    <CheckCircle className="w-12 h-12 text-neon-green mx-auto mb-2" />
                                    <p className="text-neon-green font-medium">Tidak ada duplicate score card!</p>
                                    <p className="text-sm text-zinc-500 mt-1">Semua score card di grup ini unik.</p>
                                    <Button
                                        onClick={() => setShowDuplicates(false)}
                                        className="mt-4 bg-neon-green hover:bg-neon-green/90 text-void-black"
                                    >
                                        Tutup
                                    </Button>
                                </div>
                            )}
                        </div>
                    ) : null}
                </DialogContent>
            </Dialog>

            {/* Klasemen Modal */}
            <Dialog open={showKlasmen} onOpenChange={setShowKlasmen}>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-headings flex items-center gap-2">
                            <Trophy className="w-5 h-5 text-yellow-500" />
                            Klasemen - {klasmenGroup?.name}
                        </DialogTitle>
                    </DialogHeader>

                    {/* Month Navigator */}
                    <div className="flex items-center justify-center gap-4 py-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleKlasmenMonthChange(-1)}
                            className="border-zinc-700 text-zinc-400 hover:text-white"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <div className="px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-md min-w-[160px] text-center">
                            <span className="text-white font-medium">
                                {MONTH_NAMES[klasmenMonth - 1]} {klasmenYear}
                            </span>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleKlasmenMonthChange(1)}
                            className="border-zinc-700 text-zinc-400 hover:text-white"
                            disabled={klasmenMonth === new Date().getMonth() + 1 && klasmenYear === new Date().getFullYear()}
                        >
                            <ChevronRight className="w-4 h-4" />
                        </Button>
                    </div>
                    
                    {klasmenLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <span className="spinner" />
                        </div>
                    ) : klasmenData ? (
                        <div className="space-y-6">
                            {/* Admin Create Score Card Button */}
                            <div className="flex justify-end items-center">
                                <Button
                                    onClick={handleOpenAdminCreateScorecard}
                                    className="bg-neon-green hover:bg-neon-green/90 text-void-black"
                                    data-testid="admin-create-scorecard-btn"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Buat Score Card
                                </Button>
                            </div>

                            {/* Leaderboard Table */}
                            {klasmenData.leaderboard?.entries?.length > 0 && (
                                <div className="stat-card">
                                    <h3 className="text-lg font-headings font-semibold text-white mb-4">Rankings</h3>
                                    <div className="overflow-x-auto">
                                        <table className="data-table">
                                            <thead>
                                                <tr>
                                                    <th>Rank</th>
                                                    <th>Username</th>
                                                    <th>Total Score</th>
                                                    <th>Games</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {klasmenData.leaderboard.entries.map((entry, idx) => (
                                                    <tr key={entry.user_id}>
                                                        <td>
                                                            <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full font-bold text-sm ${
                                                                idx === 0 ? 'bg-yellow-500/20 text-yellow-500' :
                                                                idx === 1 ? 'bg-zinc-400/20 text-zinc-400' :
                                                                idx === 2 ? 'bg-orange-500/20 text-orange-500' :
                                                                'bg-zinc-800 text-zinc-500'
                                                            }`}>
                                                                {idx + 1}
                                                            </span>
                                                        </td>
                                                        <td className="font-bold text-white uppercase">
                                                            {entry.username.toUpperCase()}
                                                        </td>
                                                        <td className={`font-mono font-semibold ${getScoreColorClass(entry.total_score)}`}>
                                                            {entry.total_score > 0 ? '+' : ''}{entry.total_score.toLocaleString()}
                                                        </td>
                                                        <td className="text-zinc-500">{entry.score_count}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Score Cards with Delete Button */}
                            {klasmenData.scorecards?.length > 0 ? (
                                <div>
                                    <h3 className="text-lg font-headings font-semibold text-white mb-4">
                                        Score Cards ({klasmenData.scorecards.length})
                                    </h3>
                                    <div className="grid gap-4 md:grid-cols-2">
                                        {klasmenData.scorecards.map((sc, cardIndex) => (
                                            <div key={sc.id} className="stat-card relative">
                                                {/* Score Card Number */}
                                                <div className="absolute -top-2 -left-2 w-8 h-8 rounded-full bg-neon-green flex items-center justify-center shadow-lg">
                                                    <span className="text-sm font-bold text-void-black">#{klasmenData.scorecards.length - cardIndex}</span>
                                                </div>
                                                
                                                {/* Delete Button */}
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => { setScorecardToDelete(sc); setShowDeleteScorecard(true); }}
                                                    className="absolute top-2 right-2 border-red-500/30 text-red-400 hover:bg-red-500/10 p-1 h-7 w-7"
                                                    data-testid={`delete-scorecard-${sc.id}`}
                                                >
                                                    <Trash2 className="w-3 h-3" />
                                                </Button>

                                                <div className="pl-6 pr-8 pt-1">
                                                    <p className="text-xs text-zinc-500 font-mono">
                                                        {new Date(sc.created_at).toLocaleDateString('id-ID')} {new Date(sc.created_at).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}
                                                    </p>
                                                    <p className="text-xs text-zinc-600 mb-2">
                                                        by <span className="font-bold uppercase">{sc.created_by_username.toUpperCase()}</span>
                                                    </p>
                                                </div>
                                                <div className="space-y-1">
                                                    {sc.entries.map((entry, idx) => (
                                                        <div key={idx} className="flex justify-between items-center py-1 border-b border-zinc-800 last:border-0">
                                                            <span className="text-sm font-bold text-white uppercase">
                                                                {entry.username.toUpperCase()}
                                                            </span>
                                                            <span className={`text-sm font-mono font-semibold ${getScoreColorClass(entry.score)}`}>
                                                                {entry.score > 0 ? '+' : ''}{entry.score.toLocaleString()}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-8 text-zinc-500">
                                    <Trophy className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                    <p>Belum ada score card untuk bulan ini</p>
                                </div>
                            )}
                        </div>
                    ) : null}
                </DialogContent>
            </Dialog>

            {/* Delete Scorecard Confirmation */}
            <AlertDialog open={showDeleteScorecard} onOpenChange={setShowDeleteScorecard}>
                <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-headings text-white">Hapus Score Card?</AlertDialogTitle>
                        <AlertDialogDescription className="text-zinc-400">
                            Apakah Anda yakin ingin menghapus score card ini?
                            {scorecardToDelete && (
                                <div className="mt-3 p-3 bg-zinc-800 rounded-md">
                                    <p className="text-xs text-zinc-500 mb-2">
                                        Dibuat oleh: <span className="text-white font-bold">{scorecardToDelete.created_by_username?.toUpperCase()}</span>
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        {scorecardToDelete.entries?.map((e, i) => (
                                            <span key={i} className="text-xs px-2 py-1 bg-zinc-700 rounded">
                                                {e.username.toUpperCase()}: {e.score > 0 ? '+' : ''}{e.score}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="border-zinc-700">Batal</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteScorecard}
                            className="bg-red-500 hover:bg-red-600"
                            data-testid="confirm-delete-scorecard-btn"
                        >
                            Hapus
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Admin Create Score Card Modal */}
            <Dialog open={showAdminCreateScorecard} onOpenChange={setShowAdminCreateScorecard}>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-headings">Buat Score Card (Admin)</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-zinc-400">
                            Buat score card untuk bulan ini atau bulan sebelumnya. Total skor harus = 0.
                        </p>

                        {/* Month/Year Selector */}
                        <div>
                            <Label className="form-label mb-2 block">Pilih Bulan</Label>
                            <Select 
                                value={`${adminScorecardMonth}-${adminScorecardYear}`} 
                                onValueChange={(val) => {
                                    const [m, y] = val.split('-');
                                    setAdminScorecardMonth(parseInt(m));
                                    setAdminScorecardYear(parseInt(y));
                                }}
                            >
                                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                                    <SelectValue placeholder="Pilih bulan" />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-800 border-zinc-700">
                                    {getAvailableMonths().map((m, idx) => (
                                        <SelectItem key={idx} value={`${m.month}-${m.year}`}>
                                            {MONTH_NAMES[m.month - 1]} {m.year}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* User Selection */}
                        <div>
                            <Label className="form-label mb-3 block">Pilih User</Label>
                            <div className="space-y-2 max-h-[200px] overflow-y-auto border border-zinc-800 rounded-md p-3">
                                {klasmenGroup && users.filter(u => klasmenGroup.members?.includes(u.id)).map(member => (
                                    <label 
                                        key={member.id} 
                                        className="flex items-center gap-3 p-2 rounded-md hover:bg-zinc-800 cursor-pointer"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={adminSelectedUsers.some(u => u.id === member.id)}
                                            onChange={() => handleAdminUserSelect(member.id, member.username)}
                                            className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-neon-green focus:ring-neon-green"
                                        />
                                        <span className="text-sm text-zinc-300 uppercase">{member.username.toUpperCase()}</span>
                                    </label>
                                ))}
                                {klasmenGroup && users.filter(u => klasmenGroup.members?.includes(u.id)).length === 0 && (
                                    <p className="text-sm text-zinc-500 text-center py-2">Tidak ada user di grup ini</p>
                                )}
                            </div>
                        </div>

                        {/* Score Inputs */}
                        {adminSelectedUsers.length > 0 && (
                            <div>
                                <Label className="form-label mb-3 block">Masukkan Skor</Label>
                                <div className="space-y-3">
                                    {adminSelectedUsers.map(u => (
                                        <div key={u.id} className="flex items-center gap-3">
                                            <span className="text-sm text-zinc-300 w-32 truncate uppercase">{u.username.toUpperCase()}</span>
                                            <Input
                                                type="number"
                                                step="0.1"
                                                value={adminScoreInputs[u.id] || ''}
                                                onChange={(e) => handleAdminScoreChange(u.id, e.target.value)}
                                                placeholder="Skor"
                                                className="bg-zinc-800 border-zinc-700 flex-1"
                                            />
                                        </div>
                                    ))}
                                </div>

                                {/* Total Score Display */}
                                <div className={`mt-4 p-3 rounded-md border ${isAdminScoreTotalZero() ? 'bg-neon-green/10 border-neon-green/30' : 'bg-red-500/10 border-red-500/30'}`}>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-zinc-400">Total Skor:</span>
                                        <span className={`text-lg font-mono font-bold ${isAdminScoreTotalZero() ? 'text-neon-green' : 'text-red-400'}`}>
                                            {Math.round(calculateAdminTotal() * 10) / 10}
                                        </span>
                                    </div>
                                    {!isAdminScoreTotalZero() && (
                                        <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                                            <AlertCircle className="w-3 h-3" />
                                            Total harus sama dengan 0
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="flex gap-2 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowAdminCreateScorecard(false)}
                                className="flex-1 border-zinc-700"
                            >
                                Batal
                            </Button>
                            <Button
                                onClick={handleAdminCreateScorecard}
                                disabled={adminSelectedUsers.length === 0 || adminCreateLoading || !isAdminScoreTotalZero()}
                                className="flex-1 bg-neon-green hover:bg-neon-green/90 text-void-black disabled:opacity-50"
                                data-testid="admin-submit-scorecard"
                            >
                                {adminCreateLoading ? (
                                    <span className="flex items-center gap-2">
                                        <span className="spinner w-4 h-4" />
                                        Membuat...
                                    </span>
                                ) : (
                                    `Buat untuk ${MONTH_NAMES[adminScorecardMonth - 1]}`
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Player Statistics Modal */}
            <Dialog open={showStatistics} onOpenChange={setShowStatistics}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col bg-zinc-900 border-zinc-800">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <BarChart3 className={`w-6 h-6 text-purple-400 ${isFunMode ? 'animate-pulse' : ''}`} />
                            {isFunMode ? '📊 ' : ''}Statistik Pemain - {statisticsGroup?.name}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {/* Month Navigation */}
                    <div className="flex items-center justify-center gap-4 py-2">
                        <Button
                            variant="outline"
                            size="icon"
                            onClick={() => handleStatisticsMonthChange(-1)}
                            className="border-zinc-700 text-zinc-400"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <span className="text-lg font-headings font-semibold text-white min-w-[180px] text-center">
                            {MONTH_NAMES[statisticsMonth - 1]} {statisticsYear}
                        </span>
                        <Button
                            variant="outline"
                            size="icon"
                            onClick={() => handleStatisticsMonthChange(1)}
                            className="border-zinc-700 text-zinc-400"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </Button>
                    </div>

                    <div className="flex-1 overflow-y-auto space-y-6 pr-2">
                        {statisticsLoading ? (
                            <div className="flex items-center justify-center py-12">
                                <span className="spinner" />
                            </div>
                        ) : statisticsData && statisticsData.statistics?.length > 0 ? (
                            <div className="space-y-4">
                                {/* Summary */}
                                <div className="stat-card">
                                    <p className="text-zinc-400 text-sm">
                                        Total Games Bulan Ini: <span className="text-white font-bold">{statisticsData.total_games}</span>
                                    </p>
                                </div>

                                {/* Statistics Table */}
                                <div className="stat-card overflow-x-auto">
                                    <table className="data-table w-full">
                                        <thead>
                                            <tr>
                                                <th className="text-left">{isFunMode ? '🎮 ' : ''}Pemain</th>
                                                <th className="text-center">Games</th>
                                                <th className="text-center">Total</th>
                                                <th className="text-center">Rata-rata</th>
                                                <th className="text-center">{isFunMode ? '👑 ' : ''}Menang</th>
                                                <th className="text-center">Win Rate</th>
                                                <th className="text-center">Top 3</th>
                                                <th className="text-center">Best</th>
                                                <th className="text-center">Worst</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {statisticsData.statistics.map((stat, idx) => {
                                                const avatar = getAvatarForUser(stat.username);
                                                return (
                                                    <tr key={stat.user_id} className={isFunMode ? 'fun-row-hover' : ''} style={isFunMode ? { '--row-glow': avatar.color } : {}}>
                                                        <td className="py-3">
                                                            <div className="flex items-center gap-2">
                                                                <PlayerAvatar username={stat.username} size="sm" />
                                                                <span className="font-bold text-white uppercase" style={isFunMode ? { textShadow: `0 0 10px ${avatar.color}` } : {}}>
                                                                    {stat.username.toUpperCase()}
                                                                </span>
                                                                {isFunMode && idx === 0 && <span className="text-yellow-400">👑</span>}
                                                            </div>
                                                        </td>
                                                        <td className="text-center text-zinc-400">{stat.games_played}</td>
                                                        <td className="text-center">
                                                            <FunScore score={stat.total_score} size="sm" />
                                                        </td>
                                                        <td className="text-center">
                                                            <span className={`font-mono ${stat.average_score >= 0 ? 'text-neon-green' : 'text-red-500'}`}>
                                                                {stat.average_score >= 0 ? '+' : ''}{stat.average_score}
                                                            </span>
                                                        </td>
                                                        <td className="text-center">
                                                            <span className="text-yellow-500 font-bold">{stat.wins}</span>
                                                        </td>
                                                        <td className="text-center">
                                                            <span className={`font-mono ${stat.win_rate >= 50 ? 'text-neon-green' : stat.win_rate >= 25 ? 'text-yellow-500' : 'text-red-500'}`}>
                                                                {stat.win_rate}%
                                                            </span>
                                                        </td>
                                                        <td className="text-center">
                                                            <span className="text-purple-400">{stat.top_3}</span>
                                                        </td>
                                                        <td className="text-center">
                                                            <span className="text-neon-green font-mono">
                                                                {stat.best_score !== null ? (stat.best_score > 0 ? '+' : '') + stat.best_score : '-'}
                                                            </span>
                                                        </td>
                                                        <td className="text-center">
                                                            <span className="text-red-500 font-mono">
                                                                {stat.worst_score !== null ? (stat.worst_score > 0 ? '+' : '') + stat.worst_score : '-'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Legend */}
                                <div className="text-xs text-zinc-500 px-2">
                                    <p><span className="text-yellow-500">Menang</span> = Peringkat #1 dalam game | <span className="text-purple-400">Top 3</span> = Masuk 3 besar | <span className="text-neon-green">Best/</span><span className="text-red-500">Worst</span> = Skor tertinggi/terendah dalam satu game</p>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-12 text-zinc-500">
                                <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                <p>Belum ada data statistik untuk bulan ini</p>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminDashboard;
